import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchFilters, PodcastSearchRequest } from '../types/podcast';
import { useSearchPodcasts } from '../react-query/podcast-queries';
import { mapPodcastFromApi } from '@/utils/mapper';

interface UsePodcastSearchReturn {
    // State
    inputQuery: string;
    setInputQuery: (query: string) => void;
    episodeKeywords: string[];
    setEpisodeKeywords: (keywords: string[]) => void;
    filters: SearchFilters;
    setFilters: (filters: SearchFilters | ((prev: SearchFilters) => SearchFilters)) => void;
    sortField: 'relevance' | 'popularity' | 'recent' | 'episodeCount' | 'listeners' | 'rating' | 'reviews' | 'date' | 'youtubeSubscribers';
    setSortField: (field: 'relevance' | 'popularity' | 'recent' | 'episodeCount' | 'listeners' | 'rating' | 'reviews' | 'date' | 'youtubeSubscribers') => void;
    sortOrder: 'asc' | 'desc';
    setSortOrder: (order: 'asc' | 'desc') => void;
    page: number;
    setPage: (page: number) => void;
    
    // Actions
    submitSearch: (overrideQuery?: string) => void;
    resetFilters: () => void;
    
    // Data
    podcasts: any[];
    pagination: any;
    isPending: boolean;
    error: any;
    hasMore: boolean;
    totalEstimate: number;
}

export function usePodcastSearch(): UsePodcastSearchReturn {
    const [searchParams, setSearchParams] = useSearchParams();

    // 1. Initialize State from URL
    const [inputQuery, setInputQuery] = useState(() => searchParams.get('q') || '');
    const [submittedQuery, setSubmittedQuery] = useState(() => searchParams.get('q') || '');
    const [searchSubmitted, setSearchSubmitted] = useState(() => (!!searchParams.get('q')));

    const [episodeKeywords, setEpisodeKeywords] = useState<string[]>(() => {
        const param = searchParams.get('episodeKeywords');
        return param ? param.split(',').filter(Boolean) : [];
    });

    const [filters, setFilters] = useState<SearchFilters>(() => ({
        activeWithinDays: parseInt(searchParams.get('activeWithinDays') || '90'),
        hasEmail: searchParams.get('hasEmail') !== 'false',
        hasValidRssFeed: searchParams.get('hasValidRssFeed') === 'true',
        languages: searchParams.get('languages') ? searchParams.get('languages')!.split(',') : undefined,
        minEpisodes: searchParams.get('minEpisodes') ? parseInt(searchParams.get('minEpisodes')!) : undefined,
        maxEpisodes: searchParams.get('maxEpisodes') ? parseInt(searchParams.get('maxEpisodes')!) : undefined,
        minPopularityScore: searchParams.get('minPopularityScore') ? parseInt(searchParams.get('minPopularityScore')!) : undefined,
        maxPopularityScore: searchParams.get('maxPopularityScore') ? parseInt(searchParams.get('maxPopularityScore')!) : undefined,
        minRating: searchParams.get('minRating') ? parseFloat(searchParams.get('minRating')!) : undefined,
        maxRating: searchParams.get('maxRating') ? parseFloat(searchParams.get('maxRating')!) : undefined,
        minRatingCount: searchParams.get('minRatingCount') ? parseInt(searchParams.get('minRatingCount')!) : undefined,
        maxRatingCount: searchParams.get('maxRatingCount') ? parseInt(searchParams.get('maxRatingCount')!) : undefined,
        minSubscriberCount: searchParams.get('minSubscriberCount') ? parseInt(searchParams.get('minSubscriberCount')!) : undefined,
        maxSubscriberCount: searchParams.get('maxSubscriberCount') ? parseInt(searchParams.get('maxSubscriberCount')!) : undefined,
        minListeners: searchParams.get('minListeners') ? parseInt(searchParams.get('minListeners')!) : undefined,
        maxListeners: searchParams.get('maxListeners') ? parseInt(searchParams.get('maxListeners')!) : undefined,
        minYoutubeSubscriberCount: searchParams.get('minYoutubeSubscriberCount') ? parseInt(searchParams.get('minYoutubeSubscriberCount')!) : undefined,
        author: searchParams.get('author') || undefined,
        host: searchParams.get('host') || undefined,
        minNewestItemPublishDays: searchParams.get('minNewestItemPublishDays') ? parseInt(searchParams.get('minNewestItemPublishDays')!) : undefined,
        explicit: searchParams.get('explicit') === 'true' ? true : undefined,
        excludeExplicit: searchParams.get('excludeExplicit') === 'true' ? true : undefined,
        primaryMarket: searchParams.get('primaryMarket') || undefined,
    }));

    const [sortField, setSortField] = useState<'relevance' | 'popularity' | 'recent' | 'episodeCount' | 'listeners' | 'rating' | 'reviews' | 'date' | 'youtubeSubscribers'>(
        () => (searchParams.get('sortField') as any) || 'relevance'
    );
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
        () => (searchParams.get('sortOrder') as any) || 'desc'
    );

    const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1'));
    
    const limit = 10;
    const offset = (page - 1) * limit;

    // 2. Build Search Request
    const searchRequest: PodcastSearchRequest = useMemo(() => ({
        query: submittedQuery,
        episodeKeywords: episodeKeywords.length > 0 ? episodeKeywords : undefined,
        filters,
        sortField,
        sortOrder,
        limit,
        offset
    }), [submittedQuery, episodeKeywords, filters, sortField, sortOrder, limit, offset]);

    // 3. Execute Query
    const { 
        data: searchData, 
        isFetching,
        refetch,
        error 
    } = useSearchPodcasts(searchRequest, searchSubmitted);

    const podcasts = useMemo(() => {
        if (!searchData?.success || !searchData.data.results) return [];
        return searchData.data.results.map(mapPodcastFromApi);
    }, [searchData]);
    
    const pagination = searchData?.success ? searchData.data.pagination : null;

    // 4. Effects
    
    // Handle Page/Offset Changes
    useEffect(() => {
        if (searchSubmitted && offset > 0) {
            refetch();
        }
    }, [offset, searchSubmitted, refetch]);

    // Sync URL with State
    useEffect(() => {
        const params = new URLSearchParams();
        
        if (submittedQuery) params.set('q', submittedQuery);
        if (episodeKeywords.length > 0) params.set('episodeKeywords', episodeKeywords.join(','));
        if (page > 1) params.set('page', page.toString());
        
        params.set('sortField', sortField);
        params.set('sortOrder', sortOrder);
        
        // Filter Sync
        if (filters.activeWithinDays !== 90) params.set('activeWithinDays', filters.activeWithinDays?.toString() || '');
        if (filters.hasEmail === false) params.set('hasEmail', 'false');
        if (filters.hasValidRssFeed) params.set('hasValidRssFeed', 'true');
        
        if (filters.languages?.length) params.set('languages', filters.languages.join(','));
        if (filters.minEpisodes) params.set('minEpisodes', filters.minEpisodes.toString());
        if (filters.maxEpisodes) params.set('maxEpisodes', filters.maxEpisodes.toString());
        if (filters.minPopularityScore) params.set('minPopularityScore', filters.minPopularityScore.toString());
        if (filters.maxPopularityScore) params.set('maxPopularityScore', filters.maxPopularityScore.toString());
        if (filters.minRating) params.set('minRating', filters.minRating.toString());
        if (filters.maxRating) params.set('maxRating', filters.maxRating.toString());
        if (filters.minRatingCount) params.set('minRatingCount', filters.minRatingCount.toString());
        if (filters.maxRatingCount) params.set('maxRatingCount', filters.maxRatingCount.toString());
        if (filters.minListeners) params.set('minListeners', filters.minListeners.toString());
        if (filters.maxListeners) params.set('maxListeners', filters.maxListeners.toString());
        if (filters.minSubscriberCount) params.set('minSubscriberCount', filters.minSubscriberCount.toString());
        if (filters.maxSubscriberCount) params.set('maxSubscriberCount', filters.maxSubscriberCount.toString());
        if (filters.author) params.set('author', filters.author);
        if (filters.host) params.set('host', filters.host);
        if (filters.minNewestItemPublishDays) params.set('minNewestItemPublishDays', filters.minNewestItemPublishDays.toString());
        if (filters.minYoutubeSubscriberCount) params.set('minYoutubeSubscriberCount', filters.minYoutubeSubscriberCount.toString());
        if (filters.explicit) params.set('explicit', 'true');
        if (filters.excludeExplicit) params.set('excludeExplicit', 'true');
        if (filters.primaryMarket) params.set('primaryMarket', filters.primaryMarket);

        setSearchParams(params, { replace: true });
    }, [submittedQuery, episodeKeywords, page, sortField, sortOrder, filters, setSearchParams]);

    // 5. Actions
    const submitSearch = (overrideQuery?: string) => {
        setPage(1);
        const queryToSubmit = overrideQuery ?? inputQuery;
        if (overrideQuery !== undefined) {
            setInputQuery(overrideQuery);
        }
        setSubmittedQuery(queryToSubmit);
        setSearchSubmitted(true);
        // Force refetch if query is same but user clicked search
        setTimeout(() => refetch(), 0);
    };

    const resetFilters = () => {
        setFilters({
            activeWithinDays: 90,
            hasEmail: true
        });
        setEpisodeKeywords([]);
        setPage(1);
    };

    return {
        inputQuery,
        setInputQuery,
        episodeKeywords,
        setEpisodeKeywords,
        filters,
        setFilters,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        page,
        setPage,
        submitSearch,
        resetFilters,
        podcasts,
        pagination,
        isPending: isFetching,
        error: error,
        hasMore: pagination?.hasMore ?? false,
        totalEstimate: pagination?.totalEstimate || 0
    };
}
